import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import { type Ref, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import { CalendarDayTimeline } from "@/features/calendar-events/components/CalendarDayTimeline";
import type { CalendarEvent } from "@/features/calendar-events/types/calendarEventTypes";
import { PageContent } from "@/shared/components/ui/Page";
import { calendarDate } from "@/shared/utils/calendarDate";
import { cn } from "@/shared/utils/cn";
import type { useAgendaRange } from "../hooks/useAgendaRange";
import type { AgendaDays } from "../utils/agendaDays";

const JUMP_SETTLE_MS = 400;

export interface AgendaDayStreamHandle {
  scrollToDay: (dateKey: string) => void;
}

interface AgendaDayStreamProps {
  ref?: Ref<AgendaDayStreamHandle>;
  days: AgendaDays;
  initialKey: string;
  todayKey: string;
  timezone: string;
  now: Date;
  dayData: ReturnType<typeof useAgendaRange>["dayData"];
  onRetry: () => void;
  estimateDayHeight: (dateKey: string) => number;
  onActiveDayChange: (dateKey: string) => void;
  onVisibleRangeChange: (firstDay: number, lastDay: number) => void;
  onAppointmentClick: (appointment: Appointment) => void;
  onEventClick: (event: CalendarEvent) => void;
  onGapAdd: (startAt: Date) => void;
}

export function AgendaDayStream({
  ref, days, initialKey, todayKey, timezone, now, dayData, onRetry, estimateDayHeight,
  onActiveDayChange, onVisibleRangeChange, onAppointmentClick, onEventClick, onGapAdd,
}: AgendaDayStreamProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const reported = useRef(initialKey);
  // Armed until the initial jump: the virtualizer reports offset 0 before it runs.
  const jumping = useRef<number | null>(-1);
  const [nowRequest, setNowRequest] = useState(initialKey === todayKey ? 1 : 0);
  const handledNowRequest = useRef(0);

  const reportTopDay = useCallback((instance: Virtualizer<HTMLDivElement, Element>) => {
    const range = instance.range;
    if (range) onVisibleRangeChange(range.startIndex, range.endIndex);
    // Programmatic jumps pass through other days; the settle timer reports where they land.
    if (jumping.current !== null) return;
    const probe = (instance.scrollOffset ?? 0) + 1;
    const top = instance.getVirtualItems().find((item) => item.start <= probe && item.end > probe);
    const dateKey = top ? days.dayKeyAt(top.index) : null;
    if (dateKey && dateKey !== reported.current) {
      reported.current = dateKey;
      onActiveDayChange(dateKey);
    }
  }, [days, onActiveDayChange, onVisibleRangeChange]);

  const getItemKey = useCallback((index: number) => days.dayKeyAt(index), [days]);
  const virtualizer = useVirtualizer({
    count: days.dayCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => estimateDayHeight(days.dayKeyAt(index)),
    getItemKey,
    overscan: 2,
    onChange: reportTopDay,
  });

  const jumpTo = useCallback((dateKey: string) => {
    const index = days.dayIndexOf(dateKey);
    if (index === null) return;
    window.clearTimeout(jumping.current ?? undefined);
    const settle = () => {
      if (virtualizer.isScrolling) {
        jumping.current = window.setTimeout(settle, JUMP_SETTLE_MS);
        return;
      }
      jumping.current = null;
      reportTopDay(virtualizer);
    };
    jumping.current = window.setTimeout(settle, JUMP_SETTLE_MS);
    reported.current = dateKey;
    virtualizer.scrollToIndex(index, { align: "start" });
    if (dateKey === todayKey) setNowRequest((request) => request + 1);
  }, [days, reportTopDay, todayKey, virtualizer]);

  useImperativeHandle(ref, () => ({ scrollToDay: jumpTo }), [jumpTo]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- the first scroll only follows the initial day
  useLayoutEffect(() => jumpTo(initialKey), []);

  useEffect(() => () => window.clearTimeout(jumping.current ?? undefined), []);

  // Once today has loaded, bring the now-line into view without scrolling above today's heading.
  const todayIndex = days.dayIndexOf(todayKey);
  const todayReady = todayIndex !== null && !dayData(todayKey).isLoading;
  useEffect(() => {
    if (!nowRequest || nowRequest === handledNowRequest.current || !todayReady || todayIndex === null) return;
    const frame = window.requestAnimationFrame(() => {
      handledNowRequest.current = nowRequest;
      const container = scrollRef.current;
      const item = container?.querySelector(`[data-index="${todayIndex}"]`);
      const line = item?.querySelector("[data-agenda-now]");
      const dayStart = virtualizer.getOffsetForIndex(todayIndex, "start")?.[0];
      if (!container || !item || !line || dayStart === undefined) return;
      const lineOffset = line.getBoundingClientRect().top - item.getBoundingClientRect().top;
      virtualizer.scrollToOffset(Math.max(dayStart, dayStart + lineOffset - container.clientHeight / 3));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [nowRequest, todayReady, todayIndex, scrollRef, virtualizer]);

  return (
    <PageContent ref={scrollRef} className="pt-0">
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((item) => {
        const dateKey = days.dayKeyAt(item.index);
        const day = dayData(dateKey);
        const date = calendarDate(dateKey);
        const today = dateKey === todayKey;
        return (
          <section
            key={item.key}
            data-index={item.index}
            ref={virtualizer.measureElement}
            aria-label={date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            className="absolute inset-x-0 pb-6"
            style={{ top: item.start, minHeight: day.isLoading ? estimateDayHeight(dateKey) : undefined }}
          >
            <h2 className={cn("flex items-baseline gap-2 pb-2 pt-3 text-sm font-bold", dateKey < todayKey ? "text-muted-foreground" : "text-foreground")}>
              {date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
              {today && <span className="text-xs text-primary">Today</span>}
            </h2>
            <CalendarDayTimeline
              appointments={day.appointments}
              events={day.events}
              dateKey={dateKey}
              timezone={timezone}
              now={now}
              openWindows={day.openWindows}
              gaps={day.gaps}
              isLoading={day.isLoading}
              error={day.error}
              onRetry={onRetry}
              onAppointmentClick={onAppointmentClick}
              onEventClick={onEventClick}
              onGapAdd={onGapAdd}
            />
          </section>
        );
      })}
      </div>
    </PageContent>
  );
}
